import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  TimeSeriesScale,
  Title,
  Tooltip,
  TooltipItem,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { MatProgressBarModule } from '@angular/material/progress-bar';

Chart.register(
  CategoryScale,
  LinearScale,
  LineElement,
  LineController,
  BarElement,
  TimeSeriesScale,
  BarController,
  PointElement,
  Title,
  Tooltip,
  Legend,
);

type InputDataRaw = {
  time: number;
  actual: number;
  target: number;
  product: string;
};

type LineInputData = {
  yLabel: string;
  actualLabel: string;
  targetLabel: string;

  data: InputDataRaw[];
};

type BarInputData = {
  yLabel: string;
  actualLabel: string;
  targetLabel: string;
  defaultColor: string;
  colorsMap: Array<{ product: string; color: string }>;
  data: InputDataRaw[];
};

export type WormChartDataInput = {
  shiftStartTime: Date;
  shiftEndTime: Date;
  hourlySummaryData: BarInputData;
  accumulativeData: LineInputData;
};

type RawDataType = { x: number; y: number; tooltip: string };

type ChartDataType = RawDataType[];

interface TooltipType extends TooltipItem<'bar' | 'line'> {
  raw: RawDataType;
}

@Component({
  selector: 'thred-worm-chart',
  standalone: true,
  imports: [CommonModule, MatProgressBarModule],
  templateUrl: './worm-chart.component.html',
  styleUrl: './worm-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WormChartComponent implements OnChanges {
  @ViewChild('wormChart') set canvas(canvas: ElementRef) {
    if (canvas) {
      this.myCanvas.set(canvas);
      this.createChart(canvas);
    }
  }

  myCanvas = signal<ElementRef | null>(null);
  private chart: Chart | undefined;

  @Input() chartTitle = '';
  @Input() xLabel = '';
  @Input() displayLegend = true;
  @Input() unit: 'hour' | 'minute' = 'hour';
  @Input() minsToAdd = 60;
  @Input() loading = signal(false);
  @Input({ required: true }) data: WormChartDataInput | null = null;

  ngOnChanges(): void {
    if (this.chart) {
      this.chart.destroy();
    }
    const canvas = this.myCanvas();
    if (canvas) {
      this.createChart(canvas);
    }
  }

  generateBarDataset(data: WormChartDataInput) {
    const barSectionMap = new Map<
      string, // Product
      Array<{ target: number; actual: number; time: number; tooltip: string }>
    >();
    for (const { time, actual, target, product } of data.hourlySummaryData
      .data) {
      const productMapped = barSectionMap.get(product);
      const t = new Date(time);
      const tEnd = new Date(t);
      tEnd.setMinutes(tEnd.getMinutes() + this.minsToAdd);
      const tooltipHourStart = t.getHours();
      const tooltipMinutes = t.getMinutes().toString().padStart(2, '0');
      const tooltipHourEnd = tEnd.getHours();
      const tooltipMinutesEnd = tEnd.getMinutes().toString().padStart(2, '0');
      const tooltip = `${tooltipHourStart}:${tooltipMinutes} - ${tooltipHourEnd}:${tooltipMinutesEnd}`;
      // if (this.unit == 'hour') {
      t.setMinutes(t.getMinutes() + Math.floor(this.minsToAdd / 2));
      // }
      if (productMapped) {
        productMapped.push({
          time: t.getTime(),
          target,
          actual,
          tooltip,
        });
      } else {
        barSectionMap.set(product, [
          { time: t.getTime(), target, actual, tooltip },
        ]);
      }
    }

    const barTotalTargetMissedMap = new Map<
      number, // Time
      { total: number; tooltip: string } // Target Missed Total & Tooltip (set on first load)
    >();

    const barDatas: { dataset: ChartDataType; label: string; color: string }[] =
      [];
    for (const [product, values] of barSectionMap.entries()) {
      const dataset: ChartDataType = [];
      let color = data.hourlySummaryData.defaultColor;
      const colorFound = data.hourlySummaryData.colorsMap.find(
        (c) => c.product == product,
      );
      if (colorFound) {
        color = colorFound.color;
      }
      let maxDate = new Date(data.shiftStartTime);
      for (const val of values) {
        dataset.push({ x: val.time, y: val.actual, tooltip: val.tooltip });
        const missedTargetMapped = barTotalTargetMissedMap.get(val.time);
        if (missedTargetMapped) {
          missedTargetMapped.total += val.target - val.actual;
        } else {
          barTotalTargetMissedMap.set(val.time, {
            total: val.target - val.actual,
            tooltip: val.tooltip,
          });
        }
        maxDate = val.time > maxDate.getTime() ? new Date(val.time) : maxDate;
      }

      const endTime = new Date(data.shiftEndTime);
      // loop through max value + interval till end time add push 0's if needed
      while (maxDate.getTime() < endTime.getTime()) {
        const hourTime = maxDate.getTime();

        const existing = dataset.find((d) => d.x == hourTime);
        if (!existing) {
          dataset.push({ x: hourTime, y: 0, tooltip: '' });
        }

        const hourlyTargetMap = barTotalTargetMissedMap.get(hourTime);
        if (!hourlyTargetMap) {
          barTotalTargetMissedMap.set(hourTime, {
            total: 0,
            tooltip: '',
          });
        }
        maxDate.setMinutes(maxDate.getMinutes() + this.minsToAdd);
      }
      barDatas.push({ label: product, color, dataset });
    }
    const hourlyTargetMissedData: ChartDataType = [];

    for (const [key, value] of barTotalTargetMissedMap.entries()) {
      hourlyTargetMissedData.push({
        x: key,
        y: value.total >= 0 ? value.total : 0,
        tooltip: value.tooltip,
      });
    }
    return {
      barDatas: barDatas,
      hourlyTargetMissedData,
    };
  }

  generateLineDataset(data: WormChartDataInput) {
    const lineDataMap = new Map<
      number, // Time
      { target: number; actual: number; tooltip: string }
    >();

    for (const { time, actual, target } of data.accumulativeData.data) {
      const x = new Date(time).getTime();

      const data = lineDataMap.get(x);
      if (data) {
        data.target += target;
        data.actual += actual;
      } else {
        const tooltipHourStart = new Date(x).getHours();
        const tooltipMinutes = new Date(x)
          .getMinutes()
          .toString()
          .padStart(2, '0');
        const tooltip = `${tooltipHourStart}:${tooltipMinutes}`;
        lineDataMap.set(x, { target, actual, tooltip });
      }
    }

    const keys = Array.from(lineDataMap.keys());
    keys.sort((t1, t2) => {
      return t1 - t2;
    });
    let totalActual = 0;
    let totalTarget = 0;
    const accumTotal: ChartDataType = [];
    const accumTarget: ChartDataType = [];
    for (const key of keys) {
      const value = lineDataMap.get(key);
      if (!value) {
        throw new Error('Impossible');
      }
      totalActual += value.actual;
      totalTarget += value.target;
      accumTotal.push({ x: key, y: totalActual, tooltip: value.tooltip });
      accumTarget.push({ x: key, y: totalTarget, tooltip: value.tooltip });
    }
    return { accumTarget, accumTotal };
  }

  createChart(canvas: ElementRef<HTMLCanvasElement>) {
    if (this.data) {
      // Generate bar data
      const { barDatas, hourlyTargetMissedData } = this.generateBarDataset(
        this.data,
      );
      const { accumTotal, accumTarget } = this.generateLineDataset(this.data);

      const min = new Date(this.data.shiftStartTime);
      const max = new Date(this.data.shiftEndTime);
      const diff = max.getTime() - min.getTime();
      if (this.unit == 'minute') {
        const minutesEnd = max.getMinutes();
        max.setMinutes(
          minutesEnd + (this.minsToAdd - (minutesEnd % this.minsToAdd)),
        );
        min.setTime(max.getTime() - diff);
      }
      this.chart = new Chart<'bar' | 'line', ChartDataType>(
        canvas.nativeElement,
        {
          data: {
            datasets: [
              {
                type: 'line',
                data: accumTotal,
                label: this.data.accumulativeData.actualLabel,
                pointRadius: 0,
                pointHitRadius: 5,
                borderColor: '#519872',
                backgroundColor: '#519872',
                borderWidth: 2,
                spanGaps: false,
                yAxisID: 'y2',
              },
              {
                type: 'line',
                data: accumTarget,
                label: this.data.accumulativeData.targetLabel,
                pointRadius: 0,
                pointHitRadius: 5,
                hoverBackgroundColor: '#dc3545',
                borderWidth: 2,
                spanGaps: false,
                yAxisID: 'y2',
              },
              ...barDatas
                .filter((b) => b.dataset.some((d) => d.y !== 0))
                .map(({ label, dataset, color }) => {
                  return {
                    type: 'bar' as const,
                    data: dataset,
                    label: label,
                    stack: 'a',
                    backgroundColor: color,
                    xAxisID: 'x',
                    barPercentage: 0.9,
                  };
                }),
              {
                type: 'bar',
                data: hourlyTargetMissedData,
                label: this.data.hourlySummaryData.targetLabel,
                stack: 'a',
                hoverBackgroundColor: '#ad1b1b',
                xAxisID: 'x',
                barPercentage: 0.9,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              tooltip: {
                callbacks: {
                  title: function ([context]: TooltipType[]) {
                    return context.raw.tooltip;
                  },
                },
                displayColors: false,
              },
              title: {
                display: true,
                text: this.chartTitle,
                color: '#333333',
                font: {
                  // size: isTablet ? 17 : 23,
                  size: 23,
                  weight: 'bold',
                },
              },
              legend: {
                // display:  isMobile ? false : true,
                display: this.displayLegend,
                position: 'bottom',
                labels: {
                  boxWidth: 20,
                },
              },
            },
            animation: {
              duration: 0,
            },
            scales: {
              x: {
                type: 'time',
                time: {
                  unit: this.unit,
                },
                min: min.getTime(),
                max: max.getTime(),
                grid: {
                  drawOnChartArea: false,
                },

                title: {
                  display: true,
                  text: this.xLabel,
                },
                ticks: {
                  stepSize: this.unit == 'minute' ? this.minsToAdd : undefined,
                },
                offset: false,
              },
              y: {
                type: 'linear',
                grid: {
                  drawOnChartArea: true,
                },
                title: {
                  // display:  isMobile ? false : true,
                  display: true,
                  text: this.data.hourlySummaryData.yLabel,
                },
              },
              y2: {
                title: {
                  display: true,
                  // display:  isMobile ? false : true,
                  text: this.data.accumulativeData.yLabel,
                },
                type: 'linear',
                grid: {
                  drawOnChartArea: false,
                },
                position: 'right',
              },
            },
          },
        },
      );
    }
  }
}
